import { supabase } from '../../supabase.js';
import { validateAndGetTenantUuid } from '../../utils/tenantUtils.js';
import { ServiceError } from '../../utils/errorHandler.js';

class BillingService {
  constructor() {
    this.billingPlansTable = 'billing_plans';
    this.userSubscriptionsTable = 'user_subscriptions';
    this.tokenPurchasesTable = 'token_purchases';
  }

  // Get all available billing plans
  async getBillingPlans(tenantId, planType = null) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      let query = supabase
        .from(this.billingPlansTable)
        .select('*')
        .eq('is_active', true);
      
      if (planType) {
        query = query.eq('plan_type', planType);
      }
      
      const { data, error } = await query.order('monthly_fee', { ascending: true });
      
      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      throw new ServiceError('BILLING_PLANS_FETCH_FAILED', error.message);
    }
  }

  // Get billing plan by ID
  async getBillingPlanById(planId) {
    try {
      const { data, error } = await supabase
        .from(this.billingPlansTable)
        .select('*')
        .eq('id', planId)
        .eq('is_active', true)
        .single();
      
      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      throw new ServiceError('BILLING_PLAN_FETCH_FAILED', error.message);
    }
  }

  // Create user subscription
  async createSubscription(userId, planId, tenantId, startDate = null) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      // Get plan details
      const planResult = await this.getBillingPlanById(planId);
      if (!planResult.success) {
        throw new Error('Invalid billing plan');
      }
      
      const plan = planResult.data;
      const start = startDate || new Date().toISOString().split('T')[0];
      const end = new Date();
      end.setMonth(end.getMonth() + 1);
      
      const subscriptionData = {
        user_id: userId,
        tenant_id: tenantUuid,
        plan_id: planId,
        status: 'active',
        start_date: start,
        end_date: end.toISOString().split('T')[0],
        tokens_allocated: plan.token_allowance,
        tokens_used: 0,
        tokens_remaining: plan.token_allowance,
        auto_renew: true
      };
      
      const { data, error } = await supabase
        .from(this.userSubscriptionsTable)
        .insert(subscriptionData)
        .select()
        .single();
      
      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      throw new ServiceError('SUBSCRIPTION_CREATION_FAILED', error.message);
    }
  }

  // Get user subscriptions
  async getUserSubscriptions(userId, tenantId) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      const { data, error } = await supabase
        .from(this.userSubscriptionsTable)
        .select(`
          *,
          billing_plans (
            plan_name,
            plan_type,
            plan_code,
            token_allowance,
            monthly_fee,
            features
          )
        `)
        .eq('user_id', userId)
        .eq('tenant_id', tenantUuid)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      throw new ServiceError('USER_SUBSCRIPTIONS_FETCH_FAILED', error.message);
    }
  }

  // Get active subscription for user
  async getActiveSubscription(userId, tenantId) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      const { data, error } = await supabase
        .from(this.userSubscriptionsTable)
        .select(`
          *,
          billing_plans (
            plan_name,
            plan_type,
            plan_code,
            token_allowance,
            monthly_fee,
            features
          )
        `)
        .eq('user_id', userId)
        .eq('tenant_id', tenantUuid)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      
      return { success: true, data: data || null };
    } catch (error) {
      throw new ServiceError('ACTIVE_SUBSCRIPTION_FETCH_FAILED', error.message);
    }
  }

  // Update subscription status
  async updateSubscriptionStatus(subscriptionId, status) {
    try {
      const { data, error } = await supabase
        .from(this.userSubscriptionsTable)
        .update({ 
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', subscriptionId)
        .select()
        .single();
      
      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      throw new ServiceError('SUBSCRIPTION_UPDATE_FAILED', error.message);
    }
  }

  // Cancel subscription
  async cancelSubscription(subscriptionId) {
    try {
      const { data, error } = await supabase
        .from(this.userSubscriptionsTable)
        .update({ 
          status: 'cancelled',
          auto_renew: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', subscriptionId)
        .select()
        .single();
      
      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      throw new ServiceError('SUBSCRIPTION_CANCELLATION_FAILED', error.message);
    }
  }

  // Get subscription statistics for tenant
  async getSubscriptionStats(tenantId) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenantId);
      
      const { data, error } = await supabase
        .from(this.userSubscriptionsTable)
        .select('status, tokens_allocated, tokens_used, tokens_remaining')
        .eq('tenant_id', tenantUuid);
      
      if (error) throw error;
      
      const stats = {
        total_subscriptions: data.length,
        active_subscriptions: data.filter(sub => sub.status === 'active').length,
        cancelled_subscriptions: data.filter(sub => sub.status === 'cancelled').length,
        total_tokens_allocated: data.reduce((sum, sub) => sum + (sub.tokens_allocated || 0), 0),
        total_tokens_used: data.reduce((sum, sub) => sum + (sub.tokens_used || 0), 0),
        total_tokens_remaining: data.reduce((sum, sub) => sum + (sub.tokens_remaining || 0), 0)
      };
      
      return { success: true, data: stats };
    } catch (error) {
      throw new ServiceError('SUBSCRIPTION_STATS_FETCH_FAILED', error.message);
    }
  }

  // Get billing plans by type
  async getBillingPlansByType(planType) {
    try {
      const { data, error } = await supabase
        .from(this.billingPlansTable)
        .select('*')
        .eq('plan_type', planType)
        .eq('is_active', true)
        .order('monthly_fee', { ascending: true });
      
      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      throw new ServiceError('BILLING_PLANS_BY_TYPE_FETCH_FAILED', error.message);
    }
  }

  // Compare billing plans
  async compareBillingPlans(planIds) {
    try {
      const { data, error } = await supabase
        .from(this.billingPlansTable)
        .select('*')
        .in('id', planIds)
        .eq('is_active', true)
        .order('monthly_fee', { ascending: true });
      
      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      throw new ServiceError('BILLING_PLANS_COMPARISON_FAILED', error.message);
    }
  }
}

export const billingService = new BillingService();
